import React, { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { Reveal, MaskLines, MaskLinesLoad } from "@/components/Motion";
import CtaButton from "@/components/CtaButton";
import NewsletterForm from "@/components/NewsletterForm";
import { InstagramIcon, TikTokIcon, FacebookIcon } from "@/components/SocialIcons";
import { home, teaser, brand, images, social, firstBook, getBookStage } from "@/config/site";

// Compact phase-aware countdown for the homepage teaser. Before Dec 1, 2026 it
// counts to presale (firstBook.presaleOpenAt); from Dec 1 it automatically
// retargets to the Jan 19, 2027 release (firstBook.releaseOpenAt); after
// release it swaps to a single quiet line. No manual change needed.
function PresaleCountdownCompact() {
  const presaleAt = new Date(firstBook.presaleOpenAt).getTime();
  const releaseAt = new Date(firstBook.releaseOpenAt).getTime();
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const phase = now < presaleAt ? "presale" : now < releaseAt ? "release" : "live";
  const target = phase === "presale" ? presaleAt : releaseAt;
  const remaining = target - now;

  if (phase === "live") {
    return (
      <p data-testid="home-presale-open" className="mt-6 font-body text-[11px] uppercase tracking-[0.25em] text-dh-gold">
        Available now.
      </p>
    );
  }

  return (
    <div data-testid="home-presale-countdown" className="mt-7">
      <p className="font-body text-[10px] uppercase tracking-[0.25em] text-dh-gold/80">
        {phase === "presale" ? "Presale Begins In" : "The Lost Girl Arrives In"}
      </p>
      <div className="mt-3 flex items-start justify-center gap-5 sm:gap-7">
        {[
          ["Days", Math.floor(remaining / 86400000)],
          ["Hrs", Math.floor(remaining / 3600000) % 24],
          ["Min", Math.floor(remaining / 60000) % 60],
          ["Sec", Math.floor(remaining / 1000) % 60],
        ].map(([label, value]) => (
          <div key={label} data-testid={`home-cd-${label.toLowerCase()}`} className="min-w-[2.5rem]">
            <p className="font-display text-2xl tabular-nums text-dh-cream sm:text-3xl">{String(value).padStart(2, "0")}</p>
            <p className="mt-1 font-body text-[9px] uppercase tracking-[0.2em] text-dh-muted">{label}</p>
          </div>
        ))}
      </div>
      {phase === "release" && (
        <p data-testid="home-presale-open" className="mt-4 font-body text-[10px] uppercase tracking-[0.25em] text-dh-gold/80">
          Presale is now open.
        </p>
      )}
    </div>
  );
}

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative isolate flex min-h-[100svh] items-center overflow-hidden"
    >
      {/* ---- Cinematic hero background (CSS-animated, seamless loops) ---- */}
      <div className="hero-bg" data-testid="hero-animated-bg" aria-hidden>
        <div className="hero-bg-gradient" />
        <div className="hero-bg-bokeh" />
        <div className="hero-bg-rain" />
        <div className="hero-bg-sweep" />
        <div className="hero-bg-veil" />
      </div>

      <motion.div style={{ opacity }} className="relative z-10 mx-auto w-full max-w-[1400px] px-5 sm:px-8 pt-28 pb-20">
        <motion.img
          src={brand.logoMark}
          alt="Dhamma Publishing House"
          initial={{ opacity: 0, y: 20, filter: "blur(6px)" }}
          animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
          transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
          className="mb-8 h-48 w-48 object-contain drop-shadow-[0_10px_40px_rgba(0,0,0,0.6)] sm:h-64 sm:w-64"
        />

        <h1 className="max-w-4xl font-display text-4xl leading-[1.05] tracking-tight text-dh-cream sm:text-6xl lg:text-7xl">
          <MaskLinesLoad
            lines={["Stories That Leave", "Something Behind."]}
            delay={0.35}
          />
        </h1>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.1, duration: 1 }}
          className="mt-7 max-w-xl font-serif-soft text-xl italic leading-relaxed text-dh-cream/90 sm:text-2xl"
        >
          {home.hero.supporting}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.35, duration: 0.9 }}
          className="mt-11 flex flex-col gap-4 sm:flex-row sm:items-center"
        >
          <CtaButton to={home.hero.primaryCta.to} variant="gold" testId="hero-cta-primary">
            {home.hero.primaryCta.label}
            <ArrowRight size={15} className="transition-transform duration-500 group-hover:translate-x-1" />
          </CtaButton>
          <CtaButton to={home.hero.secondaryCta.to} variant="ghost" testId="hero-cta-secondary">
            {home.hero.secondaryCta.label}
          </CtaButton>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2 text-dh-cream/50"
      >
        <ArrowDown size={20} className="animate-bounce" />
      </motion.div>
    </section>
  );
}

function WhatDhamma() {
  const w = home.whatDhamma;
  return (
    <section data-testid="what-dhamma-section" className="relative bg-gradient-to-b from-dh-bg via-[#0b1a10] to-dh-bg py-28 sm:py-36">
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-dh-gold/20 to-transparent" />
      <div className="mx-auto max-w-[1200px] px-5 sm:px-8">
        <div className="grid gap-12 md:grid-cols-12 md:gap-16">
          <div className="md:col-span-7">
            <Reveal>
              <p className="font-body text-[11px] uppercase tracking-ultra text-dh-gold">What Dhamma Is</p>
            </Reveal>
            <h2 className="mt-6 font-display text-3xl leading-tight text-dh-cream sm:text-5xl">
              <MaskLines lines={["Stories Like Ours", "Deserve to Be Told."]} />
            </h2>
          </div>
          <div className="md:col-span-5 md:pt-16">
            <Reveal delay={0.15}>
              <p className="font-body text-base leading-relaxed text-dh-cream/75 sm:text-lg">{w.body}</p>
            </Reveal>
          </div>
        </div>

        <Reveal delay={0.1}>
          <div className="mt-20 border-l border-dh-gold/40 pl-6 sm:mt-28 sm:pl-10">
            <p className="max-w-3xl font-serif-soft text-3xl italic leading-snug text-dh-cream sm:text-4xl lg:text-5xl">
              {w.pullQuote}
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function StoryTeaser() {
  const t = home.storyTeaser;
  const stage = getBookStage();
  const titleRevealed = stage >= 2 && firstBook.title;
  const coverRevealed = stage >= 3 && firstBook.coverImage;
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [reduce ? "0%" : "-8%", reduce ? "0%" : "8%"]);

  return (
    <section ref={ref} data-testid="story-teaser-section" className="relative overflow-hidden">
      <motion.div style={{ y }} className="absolute inset-0 -z-10 scale-110">
        <img src={images.teaserBayNoir} alt="Rain-slicked Bay Area street at night" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-dh-bg/75" />
        <div className="absolute inset-0 bg-gradient-to-t from-dh-bg via-transparent to-dh-bg" />
      </motion.div>

      <div className="mx-auto max-w-[1000px] px-5 sm:px-8 py-32 sm:py-44 text-center">
        <Reveal>
          <p className="font-body text-[11px] uppercase tracking-ultra text-dh-gold">{t.overline}</p>
        </Reveal>

        {coverRevealed && (
          <Reveal delay={0.08}>
            <div className="mx-auto mt-12 max-w-[260px] sm:max-w-[320px]">
              <img
                src={firstBook.coverImage}
                alt={firstBook.coverAlt}
                data-testid="home-book-cover"
                className="w-full border border-dh-gold/20 object-cover shadow-2xl shadow-black/60"
              />
            </div>
          </Reveal>
        )}

        <h2 className="mx-auto mt-10 max-w-3xl font-display text-4xl leading-tight text-dh-cream sm:text-6xl">
          <MaskLines lines={[titleRevealed ? firstBook.title : t.headline]} />
        </h2>

        {titleRevealed && (
          <Reveal delay={0.2}>
            <p className="mt-5 font-serif-soft text-xl italic text-dh-cream/85 sm:text-2xl" data-testid="home-book-byline">
              {firstBook.seriesName} &middot; By {firstBook.author}
            </p>
            <p className="mt-4 font-body text-[12px] uppercase tracking-[0.25em] text-dh-gold" data-testid="home-book-date">
              The Lost Girl Arrives {firstBook.releaseDate}
            </p>
            <p className="mt-2 font-body text-[12px] uppercase tracking-[0.25em] text-dh-cream/70" data-testid="home-book-presale">
              Presale begins {firstBook.presaleDateShort}.
            </p>
          </Reveal>
        )}

        {titleRevealed && (
          <Reveal delay={0.3}>
            <PresaleCountdownCompact />
          </Reveal>
        )}

        <div className="mx-auto mt-12 max-w-xl space-y-2">
          {teaser.lines.map((line, i) => (
            <Reveal key={i} delay={0.1 + i * 0.12}>
              <p className="font-serif-soft text-xl leading-relaxed text-dh-cream/85 sm:text-2xl">{line}</p>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.5}>
          <p className="mx-auto mt-12 font-display text-2xl italic text-dh-gold sm:text-3xl">{teaser.seal}</p>
        </Reveal>

        <Reveal delay={0.65}>
          <div className="mt-12 flex justify-center">
            <CtaButton to={t.cta.to} variant="gold" testId="teaser-cta">
              {t.cta.label}
              <ArrowRight size={15} className="transition-transform duration-500 group-hover:translate-x-1" />
            </CtaButton>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function Storyteller() {
  const s = home.storyteller;
  return (
    <section data-testid="storyteller-section" className="bg-gradient-to-b from-[#081109] via-dh-forest/45 to-[#081109] py-28 sm:py-36">
      <div className="mx-auto max-w-[1200px] px-5 sm:px-8">
        <div className="grid items-center gap-12 md:grid-cols-12 md:gap-16">
          <Reveal className="md:col-span-5">
            <div className="relative">
              <div className="absolute -inset-3 -z-10 border border-dh-gold/25" />
              <img
                src={images.buggPortrait}
                alt="Bugg, founder of Dhamma Publishing House"
                className="w-full object-cover"
              />
            </div>
          </Reveal>

          <div className="md:col-span-7 md:pl-6">
            <Reveal>
              <p className="font-body text-[11px] uppercase tracking-ultra text-dh-gold">{s.overline}</p>
            </Reveal>
            <h2 className="mt-5 font-display text-4xl text-dh-cream sm:text-6xl">
              <MaskLines lines={[s.name]} />
            </h2>
            <Reveal delay={0.1}>
              <p className="mt-3 font-serif-soft text-xl italic text-dh-cream/70">{s.subheading}</p>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-8 max-w-xl font-body text-base leading-relaxed text-dh-cream/75 sm:text-lg">
                {s.body}
              </p>
            </Reveal>
            <Reveal delay={0.3}>
              <div className="mt-10">
                <CtaButton to={s.cta.to} variant="ghost" testId="storyteller-cta">
                  {s.cta.label}
                  <ArrowRight size={15} className="transition-transform duration-500 group-hover:translate-x-1" />
                </CtaButton>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}

function ExperienceWord({ word, index }) {
  const reduce = useReducedMotion();
  const align = index % 2 === 0 ? "md:text-left md:ml-0" : "md:text-right md:ml-auto";
  return (
    <motion.p
      initial={reduce ? { opacity: 0 } : { opacity: 0, y: 48, scale: 0.97, filter: "blur(10px)" }}
      whileInView={{ opacity: 1, y: 0, scale: 1, filter: "blur(0px)" }}
      viewport={{ once: true, amount: 0.75 }}
      transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
      className={`max-w-3xl font-display text-6xl leading-none tracking-tight text-dh-cream sm:text-8xl lg:text-[9rem] ${align}`}
    >
      {word}
    </motion.p>
  );
}

function Experience() {
  const e = home.experience;
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });

  // Layers cross-fade as the visitor moves through the words — a title-sequence
  // evolution from cold urban reflections → ink/page texture → restrained warmth.
  const urbanOpacity = useTransform(scrollYProgress, [0, 0.3, 0.55], [0.9, 0.55, 0.1]);
  const inkOpacity = useTransform(scrollYProgress, [0.18, 0.5, 0.82], [0, 0.6, 0.3]);
  const warmOpacity = useTransform(scrollYProgress, [0.5, 0.82, 1], [0, 0.85, 1]);
  const greenOpacity = useTransform(scrollYProgress, [0, 0.5, 1], [0.32, 0.22, 0.28]);
  const layerY = useTransform(scrollYProgress, [0, 1], ["0%", reduce ? "0%" : "-14%"]);

  return (
    <section
      ref={ref}
      data-testid="experience-section"
      className="relative overflow-hidden py-24 sm:py-44"
    >
      <div className="absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-[#040805]" />
        <motion.img
          src={images.teaserBayNoir}
          alt=""
          aria-hidden
          style={{ opacity: reduce ? 0.2 : urbanOpacity, y: layerY }}
          className="absolute inset-0 h-[120%] w-full object-cover"
        />
        <motion.img
          src={images.experienceTexture}
          alt=""
          aria-hidden
          style={{ opacity: reduce ? 0.15 : inkOpacity, y: layerY }}
          className="absolute inset-0 h-[120%] w-full object-cover mix-blend-screen"
        />
        <motion.img
          src={images.experienceWarm}
          alt=""
          aria-hidden
          style={{ opacity: reduce ? 0.2 : warmOpacity }}
          className="absolute inset-0 h-full w-full object-cover mix-blend-screen"
        />
        <motion.div
          aria-hidden
          style={{ opacity: reduce ? 0.3 : greenOpacity }}
          className="absolute inset-0 bg-dh-forest mix-blend-multiply"
        />
        {/* vignette: dark edges, atmosphere shows through the center band */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_48%,rgba(4,8,5,0.6)_100%)]" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#040805] via-transparent to-[#040805]" />
      </div>

      <div className="mx-auto max-w-[1200px] px-5 sm:px-8">
        <Reveal>
          <p className="text-center font-body text-[11px] uppercase tracking-ultra text-dh-gold">
            The Dhamma Experience
          </p>
        </Reveal>
        <div className="mt-10 flex flex-col gap-5 sm:mt-16 sm:gap-12">
          {e.words.map((word, i) => (
            <ExperienceWord key={word} word={word} index={i} />
          ))}
        </div>
        <Reveal delay={0.1}>
          <div className="mx-auto mt-16 max-w-2xl text-center sm:mt-24">
            <div className="gold-rule mx-auto mb-10 w-24" />
            {e.closing.map((line, i) => (
              <p key={i} className="font-serif-soft text-2xl italic leading-snug text-dh-cream/90 sm:text-3xl">
                {line}
              </p>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function Community() {
  const c = home.community;
  return (
    <section data-testid="community-section" id="community" className="scroll-mt-24 bg-gradient-to-b from-dh-forest/50 via-dh-forest/30 to-dh-bg py-28 sm:py-36">
      <div className="mx-auto max-w-[900px] px-5 sm:px-8">
        <Reveal>
          <p className="font-body text-[11px] uppercase tracking-ultra text-dh-gold">Community</p>
        </Reveal>
        <h2 className="mt-6 font-display text-5xl text-dh-cream sm:text-7xl">
          <MaskLines lines={[c.headline]} />
        </h2>
        <Reveal delay={0.15}>
          <p className="mt-7 max-w-xl font-body text-base leading-relaxed text-dh-cream/75 sm:text-lg">{c.body}</p>
        </Reveal>
        <Reveal delay={0.25}>
          <div className="mt-12">
            <NewsletterForm />
          </div>
        </Reveal>
        <Reveal delay={0.35}>
          <p className="mt-12 font-serif-soft text-xl italic text-dh-cream/60">{c.footnote}</p>
        </Reveal>
      </div>
    </section>
  );
}

function SocialSection() {
  const items = [
    { name: "Instagram", href: social.instagram, Icon: InstagramIcon },
    { name: "TikTok", href: social.tiktok, Icon: TikTokIcon },
    { name: "Facebook", href: social.facebook, Icon: FacebookIcon },
  ];
  return (
    <section data-testid="social-section" className="bg-dh-bg py-24">
      <div className="mx-auto max-w-[900px] px-5 sm:px-8 text-center">
        <Reveal>
          <h2 className="font-display text-3xl text-dh-cream sm:text-4xl">Connect With Dhamma</h2>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="mt-10 flex items-center justify-center gap-4 sm:gap-6">
            {items.map(({ name, href, Icon }) => (
              <a
                key={name}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={name}
                data-testid={`social-${name.toLowerCase()}`}
                className="group inline-flex h-16 w-16 items-center justify-center border border-dh-cream/15 text-dh-cream/70 transition-all duration-500 hover:border-dh-gold hover:text-dh-gold"
              >
                <span className="scale-125">
                  <Icon />
                </span>
              </a>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <Hero />
      <WhatDhamma />
      <StoryTeaser />
      <Storyteller />
      <Experience />
      <Community />
      <SocialSection />
    </>
  );
}
